import { listPatientsByInsurer } from "@/core/patients/patient-store";
import { ensureDemoInsurerRules } from "./insurer-rules-store";
import type { Insurer, InsurerAgent, InsurerAgentInput, InsurerInput } from "./types";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { shouldAutoSeedDemoData } from "@/core/demo/is-demo-mode-enabled";

const INSURERS_KEY = "hospitol.insurers";
const AGENTS_KEY = "hospitol.insurerAgents";
const DEMO_SEED_KEY = "hospitol.insurers.demoSeeded";

function writeInsurers(list: Insurer[]): void {
  setStorageItem(INSURERS_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent("hospitol:insurers"));
}

function writeAgents(list: InsurerAgent[]): void {
  setStorageItem(AGENTS_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent("hospitol:insurers"));
}

export function ensureDemoInsurers() {
  if (!shouldAutoSeedDemoData()) return;
  if (getStorageItem(DEMO_SEED_KEY)) return;

  const raw = createInsurer({
    code: "MUT-SALAMA",
    name: "Mutuelle Salama",
    licenseNumber: "AGR-RDC-2024-001",
    phone: "+243 81 500 0001",
    email: "contact@mutsalama.demo",
    address: "Kinshasa, Gombe",
    contactPerson: "Marie Okoko",
  });

  createInsurerAgent({
    insurerId: raw.id,
    ref: "AG-001",
    firstName: "Paul",
    lastName: "Kabeya",
    email: "p.kabeya@mutsalama.demo",
    phone: "+243 81 500 0010",
  });

  const asr = createInsurer({
    code: "ASR-VIE",
    name: "Assurance Vie Plus",
    licenseNumber: "AGR-RDC-2023-088",
    phone: "+243 99 200 3300",
    email: "gestion@assurancevieplus.demo",
    address: "Lubumbashi",
    contactPerson: "Jean Mukendi",
  });

  ensureDemoInsurerRules([raw.id, asr.id]);

  setStorageItem(DEMO_SEED_KEY, new Date().toISOString());
}

export function listInsurers(activeOnly = false): Insurer[] {
  ensureDemoInsurers();
  let list = readJson<Insurer[]>(INSURERS_KEY, []);
  ensureDemoInsurerRules(list.map((i) => i.id));
  if (activeOnly) list = list.filter((i) => i.active);
  return list.sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

export function getInsurerById(id: string): Insurer | null {
  return listInsurers().find((i) => i.id === id) ?? null;
}

export function createInsurer(input: InsurerInput): Insurer {
  const now = new Date().toISOString();
  const insurer: Insurer = {
    id: `ins-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    code: input.code.trim().toUpperCase(),
    name: input.name.trim(),
    licenseNumber: input.licenseNumber?.trim() ?? "",
    phone: input.phone?.trim() ?? "",
    email: input.email?.trim() ?? "",
    address: input.address?.trim() ?? "",
    contactPerson: input.contactPerson?.trim() ?? "",
    active: input.active ?? true,
    notes: input.notes?.trim() ?? "",
    createdAt: now,
    updatedAt: now,
  };
  const list = readJson<Insurer[]>(INSURERS_KEY, []);
  list.unshift(insurer);
  writeInsurers(list);
  return insurer;
}

export function updateInsurer(id: string, input: InsurerInput): Insurer | null {
  const list = readJson<Insurer[]>(INSURERS_KEY, []);
  const index = list.findIndex((i) => i.id === id);
  if (index < 0) return null;
  list[index] = {
    ...list[index]!,
    code: input.code.trim().toUpperCase(),
    name: input.name.trim(),
    licenseNumber: input.licenseNumber?.trim() ?? "",
    phone: input.phone?.trim() ?? "",
    email: input.email?.trim() ?? "",
    address: input.address?.trim() ?? "",
    contactPerson: input.contactPerson?.trim() ?? "",
    active: input.active ?? list[index]!.active,
    notes: input.notes?.trim() ?? "",
    updatedAt: new Date().toISOString(),
  };
  writeInsurers(list);
  return list[index]!;
}

export function listInsurerAgents(insurerId?: string): InsurerAgent[] {
  ensureDemoInsurers();
  let list = readJson<InsurerAgent[]>(AGENTS_KEY, []);
  if (insurerId) list = list.filter((a) => a.insurerId === insurerId);
  return list.sort((a, b) => a.ref.localeCompare(b.ref, "fr"));
}

export function getInsurerAgentById(id: string): InsurerAgent | null {
  return readJson<InsurerAgent[]>(AGENTS_KEY, []).find((a) => a.id === id) ?? null;
}

export function createInsurerAgent(input: InsurerAgentInput): InsurerAgent {
  const now = new Date().toISOString();
  const agent: InsurerAgent = {
    id: `ina-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    insurerId: input.insurerId,
    ref: input.ref.trim().toUpperCase(),
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    email: input.email?.trim() ?? "",
    phone: input.phone?.trim() ?? "",
    active: input.active ?? true,
    createdAt: now,
    updatedAt: now,
  };
  const list = readJson<InsurerAgent[]>(AGENTS_KEY, []);
  list.unshift(agent);
  writeAgents(list);
  return agent;
}

export function countPatientsByInsurer(insurerId: string): number {
  return listPatientsByInsurer(insurerId).length;
}

export function insurerDetailPath(id: string): string {
  return `/tenant/hospitol/insurance/insurers/${id}`;
}
